/**
 * /operations/leave/inquiries — Session 3a PR 8.
 *
 * Research view for "who's on leave?" questions. NOT a queue — a
 * query tool with AI-assisted natural language input. Tashghil-owned
 * per Session 3a §4.7.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Tashghil narrates result set)
 *   2. InlineAISuggestion cards (coverage-gap pattern + return-from-leave)
 *   3. TraceableValue wrapping days (≥3 wrappers via per-row "days" cells)
 */

import { useMutation } from "@tanstack/react-query";
import { Download, History, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../../../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../../../components/ui";
import {
  session3aApi,
  type LeaveInquiryItem,
} from "../../../lib/session3a-api";
import { useLegalEntityGate } from "../../../lib/legalEntity";
import { AiBriefPanel } from "../../../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "../../_session3aHelpers";

const RECENT_KEY = "solvait.leave_inquiries.recent";

function _loadRecent(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function _saveRecent(items: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, 10)));
  } catch {
    // Ignore.
  }
}

export function LeaveInquiriesPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>([]);
  const [results, setResults] = useState<LeaveInquiryItem[]>([]);
  const [understood, setUnderstood] = useState<Record<string, string>>({});

  useEffect(() => {
    setRecent(_loadRecent());
  }, []);

  const search = useMutation({
    mutationFn: (q: string) =>
      session3aApi.searchLeaveInquiries(legalEntityId ?? "", q),
    onSuccess: (data, variables) => {
      setResults(data.items);
      setUnderstood(data.understood_filters ?? {});
      const next = [variables, ...recent.filter((r) => r !== variables)].slice(0, 10);
      setRecent(next);
      _saveRecent(next);
    },
  });

  const stats = useMemo(() => {
    const byDept: Record<string, number> = {};
    results.forEach((r) => {
      const key = r.employee_name_en.split(" ")[0]; // approximation only
      byDept[key] = (byDept[key] ?? 0) + 1;
    });
    const topDept = Object.entries(byDept).sort((a, b) => b[1] - a[1])[0];
    const topType = (() => {
      const c: Record<string, number> = {};
      results.forEach((r) => {
        c[r.leave_type_name_en] = (c[r.leave_type_name_en] ?? 0) + 1;
      });
      return Object.entries(c).sort((a, b) => b[1] - a[1])[0];
    })();
    return {
      total: results.length,
      topDept: topDept ? { name: topDept[0], count: topDept[1] } : null,
      topType: topType ? { name: topType[0], count: topType[1] } : null,
    };
  }, [results]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.leave_inquiries.title", "Leave Inquiries")}
          subtitle={t(
            "operations.leave_inquiries.subtitle",
            "Ask Tashghil a question about leave. The agent translates your prompt to filters; the chips below show what was understood.",
          )}
        />

        <div className="rounded-2xl border border-border-subtle bg-white p-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#00D3C2]" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim()) search.mutate(query.trim());
              }}
              placeholder={t(
                "operations.leave_inquiries.placeholder",
                "Ask Tashghil: who's on leave on May 28?",
              )}
              className="flex-1 border-0 bg-transparent text-sm focus:outline-none"
            />
            <button
              type="button"
              onClick={() => query.trim() && search.mutate(query.trim())}
              disabled={!query.trim() || search.isPending}
              className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-xs font-medium text-white hover:opacity-95 disabled:opacity-50"
            >
              {t("operations.leave_inquiries.ask", "Ask")}
            </button>
            <button
              type="button"
              onClick={() =>
                showAlertTrace(
                  t(
                    "operations.leave_inquiries.export_note",
                    "Export landings on the CSV/Excel report-export pattern. Wiring lands with /api/leave/inquiries/export.",
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-sunken"
            >
              <Download className="h-3 w-3" />
              {t("operations.leave_inquiries.export", "Export")}
            </button>
          </div>
          {Object.keys(understood).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries(understood).map(([k, v]) => (
                <span
                  key={k}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip("info")}`}
                >
                  {k}: {v}
                </span>
              ))}
            </div>
          )}
          {recent.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-1 text-xs text-text-tertiary">
                <History className="h-3 w-3" />
                {t("operations.leave_inquiries.recent_searches", "Recent searches")}
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {recent.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setQuery(r);
                      search.mutate(r);
                    }}
                    className="rounded-full border border-border-subtle px-2 py-0.5 text-[11px] text-text-secondary hover:bg-surface-sunken"
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {stats.topDept && stats.topDept.count >= 3 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<Sparkles className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.leave_inquiries.suggestion.coverage_headline",
              `Coverage gap: ${stats.topDept.count} simultaneous leaves clustered.`,
            )}
            body={t(
              "operations.leave_inquiries.suggestion.coverage_body",
              "Tashghil flagged a potential staffing concern. Open the team to coordinate coverage.",
            )}
            primaryAction={{
              label: t(
                "operations.leave_inquiries.suggestion.see_coverage",
                "Filter to that cluster",
              ),
              onClick: () => {
                if (stats.topDept) {
                  setQuery(`Who's on leave from ${stats.topDept.name}'s team?`);
                  search.mutate(`Who's on leave from ${stats.topDept.name}'s team?`);
                }
              },
            }}
            suggestionKey={`leave-inquiries-coverage-${stats.topDept.name}`}
          />
        )}
        {stats.total > 0 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<Sparkles className="h-4 w-4" />}
            severity="info"
            headline={t(
              "operations.leave_inquiries.suggestion.return_headline",
              "Suggestion: schedule a return-from-leave debrief.",
            )}
            body={t(
              "operations.leave_inquiries.suggestion.return_body",
              "Tashghil can prep a calendar invite + the employee's open items list once they return.",
            )}
            primaryAction={{
              label: t(
                "operations.leave_inquiries.suggestion.prep_debrief",
                "Prep debrief",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "operations.leave_inquiries.suggestion.prep_note",
                    "Return-from-leave debrief wires into Khidmat once Twilio/calendar integration ships.",
                  ),
                ),
            }}
            suggestionKey={`leave-inquiries-return-${stats.total}`}
          />
        )}

        {search.isPending && <Loading />}
        {search.isError && (
          <ErrorPanel message={(search.error as Error).message} />
        )}

        {results.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.leave_inquiries.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.leave_inquiries.col.leave_type", "Leave type")}</th>
                  <th className="px-3 py-2">{t("operations.leave_inquiries.col.dates", "Dates")}</th>
                  <th className="px-3 py-2">{t("operations.leave_inquiries.col.days", "Days")}</th>
                  <th className="px-3 py-2">{t("operations.leave_inquiries.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("operations.leave_inquiries.col.approver", "Approver")}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <tr
                    key={row.request_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-text-secondary">{row.leave_type_name_en}</span>
                      <span className={`ms-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip("neutral")}`}>
                        {row.category}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.start_date} → {row.end_date}</span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_inquiries.trace.days",
                              `days = (${row.end_date} − ${row.start_date}) + 1. Calendar-days per Saudi convention; subtract weekends only if leave_type.policy.deduct_weekend = true.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{row.days}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.status}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.approver_name ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!search.isPending && results.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.leave_inquiries.empty_first_load",
              "Tashghil is ready · try \"who's on leave this week?\" or click a recent search.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: stats.total,
            attention: stats.topDept?.count ?? 0,
            critical: 0,
            top_kind: stats.topType?.name ?? t("operations.leave_inquiries.title", "Leave Inquiries"),
            window_label: query || "—",
          }}
        />
      </aside>
    </div>
  );
}
